import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { pickBy } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { US_STATES } from 'src/app/core/constants/states';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPFormFieldType } from 'src/app/core/models/enums/DPFormFieldType';
import { DPValidationType } from 'src/app/core/models/enums/DPValidationType';
import { DPValidatorsService } from 'src/app/core/services/dp-validators.service';
import { FormParserService } from 'src/app/core/services/form-parser.service';
import { ValueForPipe } from 'src/app/shared/pipes/value-for.pipe';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MultivalueService } from './multivalue.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { MultiValueRow, MultiValueRowID } from './multivalue-row';

/**
 * Multi value component.
 *
 * The value is the a JSON string of multi-value entries.
 *   [
 *     { "field 1": "value 1", "field 2": "value 2", ... },
 *     { "field 1": "value 1", "field 2": "value 2", ... },
 *     ...
 *   ]
 *
 * Error gap entries will be excluded.
 */
@Component({
  selector: 'app-multivalue',
  standalone: true,
  imports: [
    AsyncPipe,
    ValueForPipe,
    NgTemplateOutlet,
    forwardRef(() => DynamicFormComponent),
  ],
  templateUrl: './multivalue.component.html',
  styleUrls: ['./multivalue.component.scss'],
})
export class MultivalueComponent implements OnInit, OnChanges, OnDestroy {
  @Input() field: DPFormField;

  @Input() control: UntypedFormControl;

  @Input() disabled: boolean;

  @Input() customColumnTpls: Record<string, TemplateRef<any>>;

  @Input() markAsTouched: Subject<void>;

  // When a new entry is added, removed or updated, this event will be emitted.
  @Output() onUpdate = new EventEmitter();

  @ViewChild('myModal') modalTpl: TemplateRef<any>;

  DPFormFieldType = DPFormFieldType;

  // Current values of multi-value field
  rows$: Observable<MultiValueRow[]>;

  states = US_STATES;

  form: UntypedFormGroup;

  // StartEnd date field name if the subfield exists.
  // The field is used for sorting.
  startEndFieldName: string;

  // Visible columns. These are subfields of visible
  visibleFields: DPFormField[] = [];

  triggerActionFn = this.triggerAction.bind(this);

  private updatingId: MultiValueRowID | null = null;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private modalService: NgbModal,
    private formParser: FormParserService,
    private validators: DPValidatorsService,
    private multiValueService: MultivalueService,
    private utils: UtilsService
  ) {}

  ngOnInit(): void {
    if (!this.markAsTouched) {
      this.markAsTouched = new Subject();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field?.currentValue) {
      const field = changes.field.currentValue as DPFormField;
      const startEndField = field.subFields?.find(
        (field) => field.type === DPFormFieldType.StartEndDate
      );
      this.startEndFieldName = startEndField?.name || '';
      this.multiValueService.registerField(field.name);
      this.rows$ = this.multiValueService.getRowsObservable(field.name);
    }

    if (changes.control?.currentValue) {
      const control = changes.control.currentValue as UntypedFormControl;
      this.multiValueService.updateRowsWithControlValue(
        this.field.name,
        control.value,
        this.startEndFieldName,
        this.field.validations
      );
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.getVisibleFields();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  openModal(
    content,
    rowId?: MultiValueRowID,
    initialValue?: Record<string, any>
  ): void {
    if (this.disabled) {
      return;
    }

    let updatingRow: MultiValueRow | null = null;
    if (!this.utils.isNil(rowId)) {
      this.updatingId = rowId;
      updatingRow = this.multiValueService.getRowById(this.field.name, rowId);
    } else {
      this.updatingId = null;
    }

    let additionalValidations: Record<string, ValidatorFn[]> = {};
    const currentResidenceRule = this.field.validations?.find(
      (v) => v.validationTypeId === DPValidationType.CurrentResidence
    );
    if (currentResidenceRule && this.field.subFields) {
      const fieldName = `${currentResidenceRule.pageQuestionLookupId}`;
      const rows = this.multiValueService.getRows(this.field.name);
      const currentResidenceRow = rows.find((row) =>
        row.value[fieldName]?.includes('Yes')
      );

      if (currentResidenceRow && currentResidenceRow.id !== this.updatingId) {
        const subfield = this.field.subFields.find((f) => f.name === fieldName);
        additionalValidations[fieldName] = [
          this.validators.currentResidenceSubfield(
            subfield,
            currentResidenceRule
          ),
        ];
      }
    }

    this.form = this.formParser.createDynamicForm(
      this.field.subFields,
      initialValue || updatingRow?.value || {}
    );
    this.formParser.setValidators(
      this.field.subFields,
      this.form,
      this.unsubscribe$,
      additionalValidations
    );

    this.modalService.open(content);
  }

  updateRow(row: MultiValueRow) {
    this.multiValueService.updateRow(this.field.name, row);

    this.multiValueService.sortAndValidateRows(
      this.field.name,
      this.startEndFieldName,
      this.field.validations
    );
    this.updateFormValue();
    this.onUpdate.emit();
  }

  triggerAction(ev: {
    row: MultiValueRow;
    action: 'edit' | 'delete' | 'setRow';
  }) {
    switch (ev.action) {
      case 'edit':
        this.openModal(this.modalTpl, ev.row.id);
        break;
      case 'delete':
        this.deleteRow(ev.row.id);
        break;
      case 'setRow':
        this.updateRow(ev.row);
        break;
    }
  }

  openModalForGap(content, row: MultiValueRow) {
    this.openModal(content, row.id, {
      [row.value.fieldName]: row.value.startEndDate,
    });
  }

  deleteRow(rowId: MultiValueRowID) {
    this.multiValueService.deleteRow(this.field.name, rowId);
    this.multiValueService.sortAndValidateRows(
      this.field.name,
      this.startEndFieldName,
      this.field.validations
    );
    this.updateFormValue();
    this.onUpdate.emit();
  }

  save() {
    this.form.markAllAsTouched();

    // Always emit markAsTouched first to force validation in child components
    this.markAsTouched.next();

    if (!this.form.valid) {
      return;
    }

    const formValue = pickBy(
      { ...this.form.value },
      (val) => val !== null && val !== undefined
    );

    if (this.updatingId) {
      const row = this.multiValueService.getRowById(
        this.field.name,
        this.updatingId
      );

      if (!!row.value.error) {
        this.multiValueService.updateRow(this.field.name, {
          id: this.updatingId,
          value: formValue,
        });
      } else {
        this.multiValueService.updateRow(this.field.name, {
          id: this.updatingId,
          value: { ...row.value, ...formValue },
        });
      }
    } else {
      this.multiValueService.addRow(this.field.name, formValue);
    }

    this.multiValueService.sortAndValidateRows(
      this.field.name,
      this.startEndFieldName,
      this.field.validations
    );
    this.modalService.dismissAll();

    this.updateFormValue();
    this.onUpdate.emit();
  }

  @HostListener('window:resize')
  handleWindowResize() {
    this.getVisibleFields();
  }

  /**
   * Update form value from current "values" array.
   */
  private updateFormValue() {
    if (this.control) {
      const value = this.multiValueService.getControlValueFromRows(
        this.field.name
      );
      this.control.setValue(value);
    }
  }

  /**
   * Depending on screen size, the fields visible in multi-value table will be determined.
   */
  private getVisibleFields() {
    const windowWidth = window.innerWidth;

    this.visibleFields =
      this.field?.subFields?.filter((f) => {
        if (windowWidth < 768) {
          return f.columnVisibility?.mobile !== false;
        } else if (windowWidth < 1366) {
          return f.columnVisibility?.tablet !== false;
        } else {
          return f.columnVisibility?.desktop !== false;
        }
      }) || [];
  }
}
